import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Lock, Mail, User, MapPin, Zap, Shield, Users } from "lucide-react";

const BG_IMAGE = process.env.REACT_APP_BG_IMAGE_URL || "https://customer-assets.emergentagent.com/job_client-hub-system-1/artifacts/j4k0r2d9_CARS%20%26%20COFFEE.png";

export default function LoginPage() {
  const { login, register } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [agencies, setAgencies] = useState([]);
  const [isNewAgency, setIsNewAgency] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: "",
    role: "manager",
    agency_id: "",
    new_agency_name: "",
    new_agency_city: "",
    new_agency_phone: ""
  });

  useEffect(() => {
    const fetchAgencies = async () => {
      try {
        const res = await axios.get(`${API}/agencies/public`);
        setAgencies(res.data);
      } catch (error) {
        console.error("Failed to fetch agencies:", error);
      }
    };
    fetchAgencies();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        await login(formData.email, formData.password);
        toast.success("Connexion réussie");
      } else {
        // Creating new agency
        if (isNewAgency) {
          if (!formData.new_agency_city) {
            toast.error("Veuillez entrer la ville de l'agence");
            setLoading(false);
            return;
          }
          // First create the agency
          const agencyRes = await axios.post(`${API}/agencies/public/create`, {
            name: formData.new_agency_name || `Horsepower ${formData.new_agency_city}`,
            city: formData.new_agency_city,
            phone: formData.new_agency_phone || ""
          });
          // Then register user with new agency
          await register({
            ...formData,
            agency_id: agencyRes.data.id,
            role: "admin" // New agency creator gets admin rights
          });
          toast.success("Agence et compte créés avec succès !");
        } else {
          if (!formData.agency_id) {
            toast.error("Veuillez sélectionner votre agence");
            setLoading(false);
            return;
          }
          await register(formData);
          toast.success("Compte créé avec succès");
        }
      }
    } catch (error) {
      console.error("Login error:", error);
      let message = "Une erreur est survenue";
      if (error.response?.data?.detail) {
        message = error.response.data.detail;
      } else if (error.code === "ERR_NETWORK") {
        message = "Impossible de contacter le serveur. Vérifiez votre connexion.";
      } else if (error.message) {
        message = error.message;
      }
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Branding & Image */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-3/5 relative overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${BG_IMAGE})` }}
        />
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-transparent" />
        
        {/* Content */}
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          {/* Espace vide en haut */}
          <div></div>
          
          {/* Hero Text */}
          <div className="max-w-lg">
            <h1 
              className="text-5xl xl:text-6xl font-bold mb-6 leading-tight"
              style={{ fontFamily: 'Bebas Neue, sans-serif', color: '#FEED00', letterSpacing: '0.02em' }}
            >
              GÉREZ VOS CENTRES<br />
              EN TOUTE SIMPLICITÉ
            </h1>
            <p className="text-gray-400 text-lg leading-relaxed">
              Plateforme de gestion multi-agences pour optimiser vos rendez-vous, 
              clients et performances.
            </p>
          </div>
          
          {/* Features */}
          <div className="flex gap-8">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#FEED00]/10 flex items-center justify-center">
                <Users className="w-5 h-5 text-[#FEED00]" />
              </div>
              <div>
                <p className="text-white font-medium text-sm">Multi-agences</p>
                <p className="text-gray-500 text-xs">4 centres connectés</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#FEED00]/10 flex items-center justify-center">
                <Zap className="w-5 h-5 text-[#FEED00]" />
              </div>
              <div>
                <p className="text-white font-medium text-sm">Temps réel</p>
                <p className="text-gray-500 text-xs">Synchronisation instant</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#FEED00]/10 flex items-center justify-center">
                <Shield className="w-5 h-5 text-[#FEED00]" />
              </div>
              <div>
                <p className="text-white font-medium text-sm">Sécurisé</p>
                <p className="text-gray-500 text-xs">Données protégées</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 xl:w-2/5 bg-black flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          {/* Mobile - rien */}

          {/* Desktop - Small logo accent */}
          <div className="hidden lg:block absolute top-8 right-8">
            <div className="w-10 h-10 rounded-xl bg-[#FEED00]/10 flex items-center justify-center">
              <svg viewBox="0 0 24 24" className="w-6 h-6 text-[#FEED00]" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
              </svg>
            </div>
          </div>

          {/* Form Header with brand accent */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-8 w-1 bg-[#FEED00] rounded-full" />
              <h2 
                className="text-3xl font-bold"
                style={{ fontFamily: 'Bebas Neue, sans-serif', color: '#FEED00', letterSpacing: '0.05em' }}
              >
                {isLogin ? "CONNEXION" : "CRÉER UN COMPTE"}
              </h2>
            </div>
            <p className="text-gray-500 pl-4">
              {isLogin 
                ? "Accédez à votre espace de gestion"
                : "Rejoignez votre équipe"
              }
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {!isLogin && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-gray-300 text-sm">Nom complet</Label>
                  <div className="relative">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600" />
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      placeholder="Jean Dupont"
                      value={formData.name}
                      onChange={handleChange}
                      className="pl-11 h-12 bg-[#0A0A0A] border-[#1A1A1A] text-white placeholder:text-gray-600 focus:border-[#FEED00] focus:ring-[#FEED00]/20 rounded-xl"
                      required={!isLogin}
                      data-testid="register-name-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-gray-300 text-sm">Type de compte</Label>
                  <Select 
                    value={isNewAgency ? "new_agency" : formData.role} 
                    onValueChange={(value) => {
                      if (value === "new_agency") {
                        setIsNewAgency(true);
                        setFormData(prev => ({ ...prev, role: "admin" }));
                      } else {
                        setIsNewAgency(false);
                        setFormData(prev => ({ ...prev, role: value }));
                      }
                    }}
                  >
                    <SelectTrigger 
                      className="h-12 bg-[#0A0A0A] border-[#1A1A1A] text-white focus:border-[#FEED00] focus:ring-[#FEED00]/20 rounded-xl"
                      data-testid="register-role-select"
                    >
                      <Shield className="w-4 h-4 mr-2 text-gray-600" />
                      <SelectValue placeholder="Choisissez votre rôle" />
                    </SelectTrigger>
                    <SelectContent style={{ background: '#0A0A0A', borderColor: '#1A1A1A' }}>
                      <SelectItem value="new_agency" className="text-gray-200 focus:bg-[#FEED00]/20 focus:text-[#FEED00]">
                        <div className="flex flex-col">
                          <span className="font-medium">Créer une nouvelle agence</span>
                          <span className="text-xs text-gray-500">Droits admin sur votre agence</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="manager" className="text-gray-200 focus:bg-[#FEED00]/20 focus:text-[#FEED00]">
                        <div className="flex flex-col">
                          <span className="font-medium">Manager d'agence</span>
                          <span className="text-xs text-gray-500">Accès complet à une agence existante</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="mecano" className="text-gray-200 focus:bg-[#FEED00]/20 focus:text-[#FEED00]">
                        <div className="flex flex-col">
                          <span className="font-medium">Mécano / Atelier</span>
                          <span className="text-xs text-gray-500">Planning et clients uniquement</span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* New agency fields */}
                {isNewAgency ? (
                  <>
                    <div className="space-y-2">
                      <Label className="text-gray-300 text-sm">Ville de l'agence *</Label>
                      <div className="relative">
                        <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600" />
                        <Input
                          name="new_agency_city"
                          type="text"
                          placeholder="Ex: Lyon, Toulouse..."
                          value={formData.new_agency_city}
                          onChange={handleChange}
                          className="pl-11 h-12 bg-[#0A0A0A] border-[#1A1A1A] text-white placeholder:text-gray-600 focus:border-[#FEED00] focus:ring-[#FEED00]/20 rounded-xl"
                          required
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-300 text-sm">Nom de l'agence (optionnel)</Label>
                      <Input
                        name="new_agency_name"
                        type="text"
                        placeholder="Ex: Horsepower Lyon"
                        value={formData.new_agency_name}
                        onChange={handleChange}
                        className="h-12 bg-[#0A0A0A] border-[#1A1A1A] text-white placeholder:text-gray-600 focus:border-[#FEED00] focus:ring-[#FEED00]/20 rounded-xl"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-300 text-sm">Téléphone (optionnel)</Label>
                      <Input
                        name="new_agency_phone"
                        type="tel"
                        placeholder="Ex: 04 XX XX XX XX"
                        value={formData.new_agency_phone}
                        onChange={handleChange}
                        className="h-12 bg-[#0A0A0A] border-[#1A1A1A] text-white placeholder:text-gray-600 focus:border-[#FEED00] focus:ring-[#FEED00]/20 rounded-xl"
                      />
                    </div>
                  </>
                ) : (
                  <div className="space-y-2">
                    <Label htmlFor="agency" className="text-gray-300 text-sm">Votre agence</Label>
                    <Select 
                      value={formData.agency_id} 
                      onValueChange={(value) => setFormData({ ...formData, agency_id: value })}
                    >
                      <SelectTrigger 
                        className="h-12 bg-[#0A0A0A] border-[#1A1A1A] text-white focus:border-[#FEED00] focus:ring-[#FEED00]/20 rounded-xl"
                        data-testid="register-agency-select"
                      >
                        <MapPin className="w-4 h-4 mr-2 text-gray-600" />
                        <SelectValue placeholder="Sélectionnez votre agence" />
                      </SelectTrigger>
                      <SelectContent style={{ background: '#0A0A0A', borderColor: '#1A1A1A' }}>
                        {agencies.map((agency) => (
                          <SelectItem 
                            key={agency.id} 
                            value={agency.id} 
                            className="text-gray-200 focus:bg-[#FEED00]/20 focus:text-[#FEED00]"
                          >
                            {agency.city}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </>
            )}

            <div className="space-y-2">
              <Label htmlFor="email" className="text-gray-300 text-sm">Adresse email</Label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="vous@horsepower-garage.fr"
                  value={formData.email}
                  onChange={handleChange}
                  className="pl-11 h-12 bg-[#0A0A0A] border-[#1A1A1A] text-white placeholder:text-gray-600 focus:border-[#FEED00] focus:ring-[#FEED00]/20 rounded-xl"
                  required
                  data-testid="login-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-gray-300 text-sm">Mot de passe</Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600" />
                <Input
                  id="password"
                  name="password"
                  type="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleChange}
                  className="pl-11 h-12 bg-[#0A0A0A] border-[#1A1A1A] text-white placeholder:text-gray-600 focus:border-[#FEED00] focus:ring-[#FEED00]/20 rounded-xl"
                  required
                  data-testid="login-password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full h-12 font-bold text-base rounded-xl transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
              style={{ 
                background: '#FEED00', 
                color: '#000000',
                fontFamily: 'Bebas Neue, sans-serif',
                letterSpacing: '0.1em'
              }}
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                  {isLogin ? "CONNEXION..." : "CRÉATION..."}
                </span>
              ) : (
                isLogin ? "SE CONNECTER" : "CRÉER LE COMPTE"
              )}
            </Button>
          </form>

          {/* Toggle Auth Mode */}
          <div className="mt-8 pt-6 border-t border-[#1A1A1A] text-center">
            <button
              type="button"
              onClick={() => setIsLogin(!isLogin)}
              className="text-sm text-gray-500 hover:text-[#FEED00] transition-colors"
              data-testid="toggle-auth-mode"
            >
              {isLogin 
                ? <>Pas encore de compte ? <span className="text-[#FEED00]">S'inscrire</span></>
                : <>Déjà un compte ? <span className="text-[#FEED00]">Se connecter</span></>
              }
            </button>
          </div>

          {/* Footer */}
          <div className="mt-12 text-center">
            <p className="text-gray-600 text-xs">
              © 2026 Horsepower Garage. Tous droits réservés.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
